/**
 * Collapsible accordion pane for Cycle Builder progressive disclosure.
 */
import React, { useId } from "react";
import { ChevronDown } from "../icons";
import { cb, cbCn } from "./cycleBuilderTheme";

export function CollapsiblePane({
  open,
  onToggle,
  title,
  summary,
  children,
  disabled = false,
  isDarkMode = false,
  isMobile = false,
  pad = "md",
  bareBody = false,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  /** Compact chip / text shown in the header. */
  summary?: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
  isDarkMode?: boolean;
  isMobile?: boolean;
  pad?: "sm" | "md";
  /** When true, omit body padding (child provides its own layout). */
  bareBody?: boolean;
}) {
  const panelId = useId();
  const headerId = `${panelId}-header`;
  const contentId = `${panelId}-content`;

  return (
    <div className={cb.accordionShell(isDarkMode)}>
      <button
        type="button"
        id={headerId}
        aria-expanded={open}
        aria-controls={contentId}
        disabled={disabled}
        onClick={() => {
          if (!disabled) onToggle();
        }}
        className={cbCn(cb.accordionHeader(isDarkMode, open, disabled), isMobile ? "min-h-[48px]" : "")}
      >
        <span
          className={cbCn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-transform duration-200",
            open ? "rotate-0" : "-rotate-90",
            isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"
          )}
          aria-hidden
        >
          <ChevronDown className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cbCn(
              "block font-semibold",
              isMobile ? "text-base" : "text-base sm:text-lg",
              isDarkMode ? "text-slate-100" : "text-slate-900"
            )}
          >
            {title}
          </span>
        </span>
        {summary != null && summary !== "" && (
          <span
            className={cbCn(
              "max-w-[50%] truncate text-xs font-medium sm:max-w-none sm:text-sm",
              isDarkMode ? "text-slate-400" : "text-slate-500"
            )}
          >
            {summary}
          </span>
        )}
      </button>
      <div
        id={contentId}
        role="region"
        aria-labelledby={headerId}
        hidden={!open}
        className={cbCn(
          !open && "hidden",
          open && "origin-top opacity-100 transition-opacity duration-200",
          bareBody
            ? cbCn("border-t", isDarkMode ? "border-slate-700" : "border-slate-200")
            : cb.accordionBody(isDarkMode, pad)
        )}
      >
        {children}
      </div>
    </div>
  );
}
